/**
 * ProgressBar Component - Visual progress display for LooperNode
 *
 * Shows iteration progress with:
 * - Visual progress bar with status-based colors
 * - Status text describing current state
 * - Percentage display
 * - Current item label (during execution)
 *
 * Color scheme:
 * - Gray (idle): Ready to start
 * - Blue (planning): AI generating plan
 * - Indigo (executing): Processing items
 * - Yellow (paused): Waiting for user action
 * - Green (complete): All done
 * - Red (error): Something went wrong
 */

import type { LooperStatus } from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

interface ProgressBarProps {
  /** Current item index (0-based, so first item is 0) */
  current: number;
  /** Total number of items to process */
  total: number;
  /** Current execution status */
  status: LooperStatus;
  /** Optional label for the current item being processed */
  currentItemLabel?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Background colors for the progress bar fill based on status
 */
const statusColors: Record<LooperStatus, string> = {
  idle: 'bg-gray-200',
  planning: 'bg-blue-400',
  executing: 'bg-indigo-500',
  paused: 'bg-yellow-500',
  complete: 'bg-green-500',
  error: 'bg-red-500',
};

/**
 * Text colors for the status label based on status
 */
const statusTextColors: Record<LooperStatus, string> = {
  idle: 'text-gray-600',
  planning: 'text-blue-600',
  executing: 'text-indigo-600',
  paused: 'text-yellow-600',
  complete: 'text-green-600',
  error: 'text-red-600',
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * ProgressBar - Displays iteration progress with status
 *
 * Usage:
 * ```tsx
 * <ProgressBar
 *   current={3}
 *   total={10}
 *   status="executing"
 *   currentItemLabel="document-4.pdf"
 * />
 * ```
 */
export function ProgressBar({
  current,
  total,
  status,
  currentItemLabel,
}: ProgressBarProps) {
  // Calculate percentage (handle edge case of total = 0)
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  // Get the status label to display
  const getStatusLabel = (): string => {
    switch (status) {
      case 'idle':
        return 'Ready';
      case 'planning':
        return 'Planning...';
      case 'executing':
        return `Processing ${current + 1} of ${total}`;
      case 'paused':
        return 'Paused - Action needed';
      case 'complete':
        return `Complete (${total} items)`;
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="space-y-2">
      {/* Progress bar track and fill */}
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${statusColors[status]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Status text and percentage */}
      <div className="flex items-center justify-between text-xs">
        <span className={`font-medium ${statusTextColors[status]}`}>
          {getStatusLabel()}
        </span>
        <span className="text-gray-400">{percentage}%</span>
      </div>

      {/* Current item label - only show during execution */}
      {status === 'executing' && currentItemLabel && (
        <div className="text-[10px] text-gray-500 truncate">
          Current: {currentItemLabel}
        </div>
      )}
    </div>
  );
}
